import i18next, { TFunction } from 'i18next';
import {
  C_Report_Error_Code,
  C_Email_Msg_Doctype,
  C_History_Pdf_Doctype,
  C_Report,
  Wa_PdfFileOfInvoiceInput,
  C_Correspondence_Language,
} from '../../../graphql/generatedModel';
import { localeFormatterHelper } from '../../../shared/helpers/formatter/localeFormatter.helper';
import { getI18nLangFromCorrespondenceLanguage } from '../../helpers/localization/correspondenceLanguage.helper';
import { companyConfigService } from '../../services/companyConfig/companyConfig.service';
import { IApiError, LocalizationKey } from '../../../shared/responseHandler/responseHandler';
import {
  snackbarService,
  ISnackbarOptions,
} from '../../components/snackbar/service/snackbar.service';
import { PrintInvoiceReportsRes } from '../../../pages/invoice/services/invoiceList.service';
import { ListWAPdfHistoryReportRes } from './reports.service';
import { storageHelper } from '../../helpers/storage';

const getTranslatedEmailText = (
  translations: Array<{ content: string; languageId: string }> | null | undefined,
  correspondenceLanguage: C_Correspondence_Language,
): string | undefined => {
  if (!translations?.length) return undefined;

  const bcp47 = getI18nLangFromCorrespondenceLanguage(correspondenceLanguage);
  const cachedLanguages = storageHelper.memory.getItem(
    'configsData.dictTranslationLanguages',
  ) as Array<{ id: string; name?: string | null }> | null;

  if (!cachedLanguages?.length) return undefined;

  const matchedLanguage = cachedLanguages.find((lang) =>
    lang.name?.toLowerCase().startsWith(bcp47.toLowerCase()),
  );
  if (!matchedLanguage) return undefined;

  const translation = translations.find((t) => t.languageId === matchedLanguage.id);
  return translation?.content || undefined;
};

export const getPdfDocTypeFromEmailDocType = (
  emailDocType: C_Email_Msg_Doctype,
): C_History_Pdf_Doctype | undefined => {
  switch (emailDocType) {
    case C_Email_Msg_Doctype.EML_INVOICE:
      return C_History_Pdf_Doctype.PDF_INVOICE;

    case C_Email_Msg_Doctype.EML_ORDER:
      return C_History_Pdf_Doctype.PDF_ORDER;

    case C_Email_Msg_Doctype.EML_OFFER:
      return C_History_Pdf_Doctype.PDF_OFFER;

    default:
      return undefined;
  }
};

export const getFileNameForEmailWithDocument = (
  t: TFunction,
  emailDocType: C_Email_Msg_Doctype,
  documentNo: number,
  reportId: C_Report,
  correspondenceLanguage?: C_Correspondence_Language,
): string => {
  const tFixed = correspondenceLanguage
    ? i18next.getFixedT(getI18nLangFromCorrespondenceLanguage(correspondenceLanguage))
    : t;
  let attachmentFileName: string;

  switch (emailDocType) {
    case C_Email_Msg_Doctype.EML_ORDER:
      attachmentFileName = `${tFixed('common.delivery_note')}_${documentNo}.pdf`;
      break;

    case C_Email_Msg_Doctype.EML_OFFER:
      attachmentFileName = `${tFixed('offer.offer')}_${documentNo}.pdf`;
      break;

    case C_Email_Msg_Doctype.EML_INVOICE:
      switch (reportId) {
        case C_Report.R9_INVOICE_A4:
        case C_Report.R19_INVOICE_A4_DETAILED:
        case C_Report.R31_INVOICE_A4_ARTICLE_GROUPING:
        case C_Report.R39_INVOICE_A4_CUSTOMER_GROUPING:
          attachmentFileName = `${tFixed('invoice.invoice')}_${documentNo}.pdf`;
          break;

        case C_Report.R18_INVOICE_PAYMENTSLIP:
        case C_Report.R59_INVOICE_PAYMENTSLIP_WITH_QR:
          attachmentFileName = `${tFixed('invoice.payment_slip')}_${documentNo}.pdf`;
          break;

        case C_Report.R17_INVOICE_SUMMARY:
        case C_Report.R52_DELIVERY_OVERVIEW:
          attachmentFileName = `${tFixed('invoice.invoice_summary')}_${documentNo}.pdf`;
          break;

        default:
          attachmentFileName = `${tFixed('common.report')}.pdf`;
      }
      break;

    default:
      attachmentFileName = `${tFixed('common.report')}.pdf`;
  }

  return attachmentFileName;
};

export const getFileNameForInvoiceXml = (documentNo: number): string => {
  return `xrechnung_${documentNo}.xml`;
};

export const getTextForEmailWithDocument = (
  t: TFunction,
  emailDocType: C_Email_Msg_Doctype,
  documentNoReplacement?: number,
  correspondenceLanguage?: C_Correspondence_Language,
): string => {
  const tFixed = correspondenceLanguage
    ? i18next.getFixedT(getI18nLangFromCorrespondenceLanguage(correspondenceLanguage))
    : t;
  const configs = companyConfigService.getCachedConfigs();
  let emailText = '';

  switch (emailDocType) {
    case C_Email_Msg_Doctype.EML_INVOICE:
      if (configs?.isDefaultEmailTextForInvoice) {
        emailText = tFixed('invoice.email_message');
      } else {
        emailText =
          (correspondenceLanguage &&
            getTranslatedEmailText(
              configs?.invoiceEmailTextTranslations,
              correspondenceLanguage,
            )) ??
          configs?.customEmailTextForInvoice ??
          '';
      }
      if (documentNoReplacement != null) {
        emailText = emailText.replace('[FakturaNr]', String(documentNoReplacement));
      }
      break;

    case C_Email_Msg_Doctype.EML_ORDER:
      if (configs?.isDefaultEmailTextForOrder) {
        emailText = tFixed('order.email_message');
      } else {
        emailText =
          (correspondenceLanguage &&
            getTranslatedEmailText(configs?.orderEmailTextTranslations, correspondenceLanguage)) ??
          configs?.customEmailTextForOrder ??
          '';
      }
      if (documentNoReplacement != null) {
        emailText = emailText.replace('[LieferscheinNr]', String(documentNoReplacement));
      }
      break;

    case C_Email_Msg_Doctype.EML_OFFER:
      if (configs?.isDefaultEmailTextForOffer) {
        emailText = tFixed('offer.email_message');
      } else {
        emailText =
          (correspondenceLanguage &&
            getTranslatedEmailText(configs?.offerEmailTextTranslations, correspondenceLanguage)) ??
          configs?.customEmailTextForOffer ??
          '';
      }
      if (documentNoReplacement != null) {
        emailText = emailText.replace('[OfferNr]', String(documentNoReplacement));
      }
      break;

    case C_Email_Msg_Doctype.EML_REMINDER_OF_INVOICE:
      if (configs?.isDefaultEmailTextForDebtor) {
        emailText = tFixed('debitor.emailMessage');
      } else {
        emailText =
          (correspondenceLanguage &&
            getTranslatedEmailText(
              configs?.debtReminderEmailTextTranslations,
              correspondenceLanguage,
            )) ??
          configs?.customEmailTextForDebtor ??
          '';
      }
      break;
  }

  return emailText;
};

export const getSubjectForEmailWithDocument = (
  t: TFunction,
  emailDocType: C_Email_Msg_Doctype,
  documentNo?: number,
  documentDate?: Date,
  correspondenceLanguage?: C_Correspondence_Language,
): string => {
  const tFixed = correspondenceLanguage
    ? i18next.getFixedT(getI18nLangFromCorrespondenceLanguage(correspondenceLanguage))
    : t;
  let subject = '';

  switch (emailDocType) {
    case C_Email_Msg_Doctype.EML_INVOICE:
      subject =
        documentNo !== undefined
          ? `${tFixed('common.invoice')} ${documentNo}` +
            (documentDate !== undefined
              ? ` ${tFixed('common.for').toLowerCase()} ${localeFormatterHelper.formatDate(
                  documentDate,
                )}`
              : '')
          : tFixed('common.invoice');
      break;

    case C_Email_Msg_Doctype.EML_ORDER:
      subject =
        documentNo !== undefined
          ? `${tFixed('common.order')} ${documentNo}` +
            (documentDate !== undefined
              ? ` ${tFixed('common.for').toLowerCase()} ${localeFormatterHelper.formatDate(
                  documentDate,
                )}`
              : '')
          : tFixed('common.order');
      break;

    case C_Email_Msg_Doctype.EML_OFFER:
      subject =
        documentNo !== undefined
          ? `${tFixed('common.offer')} ${documentNo}` +
            (documentDate !== undefined
              ? ` ${tFixed('common.for').toLowerCase()} ${localeFormatterHelper.formatDate(
                  documentDate,
                )}`
              : '')
          : tFixed('common.offer');
      break;

    case C_Email_Msg_Doctype.EML_REMINDER_OF_INVOICE:
      subject = tFixed('debitor.reminder');
      break;
  }

  return subject;
};

export const getFilesDataToExtractXmlFromPdfOfInvoices = (
  reportsData: ListWAPdfHistoryReportRes['reports'],
): Array<Wa_PdfFileOfInvoiceInput> => {
  const justInvoicePdfsData = reportsData.filter(
    (pdfFileData) =>
      pdfFileData.reportId === C_Report.R9_INVOICE_A4 ||
      pdfFileData.reportId === C_Report.R19_INVOICE_A4_DETAILED ||
      pdfFileData.reportId === C_Report.R31_INVOICE_A4_ARTICLE_GROUPING ||
      pdfFileData.reportId === C_Report.R39_INVOICE_A4_CUSTOMER_GROUPING,
  );

  return justInvoicePdfsData.map((invoicePdfData) => {
    return {
      invoiceId: invoicePdfData.documentId,
      pdfFile: {
        s3Bucket: invoicePdfData.bucket,
        s3Key: invoicePdfData.s3Key ?? '',
      },
    };
  });
};

export const responseErrorHandlerForReportsPreview = (
  err: IApiError,
): {
  message?: LocalizationKey;
  reportToSentry: boolean;
} => {
  snackbarService.pub.hide();
  let snackArgs: ISnackbarOptions = {
    content: i18next.t('common.server_error'),
    type: 'error',
  };
  let reportToSentry = true;
  if (err.code === C_Report_Error_Code.CUSTOMER_SETTING_DO_NOT_PRINT) {
    snackArgs = {
      content: i18next.t('common.customer_not_uses_this_print_form'),
      type: 'info',
    };
    reportToSentry = false;
  }
  if (err.code === C_Report_Error_Code.PDF_IS_NOT_GENERATED) {
    snackArgs = {
      content: i18next.t('common.report_is_generating_now'),
      type: 'warning',
    };
    reportToSentry = false;
  }
  snackbarService.pub.show(snackArgs);
  return {
    reportToSentry,
  };
};

export const responseErrorHandlerForReportsPrinting = (
  err: IApiError,
): {
  message?: LocalizationKey;
  reportToSentry: boolean;
} => {
  snackbarService.pub.hide();
  let snackArgs: ISnackbarOptions = {
    content: i18next.t('common.server_error'),
    type: 'error',
  };
  let reportToSentry = true;
  if (err.code === C_Report_Error_Code.CUSTOMER_SETTING_DO_NOT_PRINT) {
    snackArgs = {
      content: i18next.t('common.customer_not_uses_this_print_form'),
      type: 'info',
    };
    reportToSentry = false;
  }
  if (err.code === C_Report_Error_Code.PDF_IS_NOT_GENERATED) {
    snackArgs = {
      content: i18next.t('common.report_is_generating_now'),
      type: 'warning',
    };
    reportToSentry = false;
  }
  if (err.code === C_Report_Error_Code.REPORT_NOT_CONNECTED_TO_PRINTER) {
    snackArgs = {
      content: i18next.t('common.assign_printers_first'),
      type: 'warning',
      link: {
        text: i18next.t('settings.options'),
        href: '/settings/print-forms/printers-assignment',
      },
    };
    reportToSentry = false;
  }
  snackbarService.pub.show(snackArgs);
  return {
    reportToSentry,
  };
};

export const successHandlerForReportsPrinting = (
  data: PrintInvoiceReportsRes,
): string | undefined => {
  snackbarService.pub.hide();
  const { countCreated, countErrors } = data;
  const noErrors = countErrors === 0;
  if (noErrors) {
    snackbarService.pub.show({
      type: 'success',
      content: i18next.t('common.print_jobs_created', { countCreated }),
    });
  } else {
    snackbarService.pub.show({
      type: 'warning',
      content: i18next.t('common.print_jobs_created_with_errors', {
        countCreated,
        countErrors,
      }),
    });
  }
  return undefined;
};
